<template>
  <div id="homepage">
    <div v-if="loading" id="loading">
      <i class="material-icons">hourglass_full</i>
      Loading...
    </div>

    <template v-else>
      <!-- Header: if logged in, "<username>'s RPs" otherwise "Welcome, stranger" -->
      <template v-if="user.anon">
        <header title="Let's RPNow">
          <aside>Let's</aside>
          <h1>RPNow</h1>
        </header>
        <h1>Welcome, stranger</h1>
      </template>
      <template v-else="user.anon">
        <h1>{{myUsername + "'s RPs"}}</h1>
      </template>

      <!-- If logged in, show "logout" button under header -->
      <button id="logout-button" v-if="!user.anon" @click="confirmLogout">Log out</button>

      <!-- If you can create an RP, ("anon create" is set, or logged in with privileges) show "create" button -->
      <div v-if="canCreate" id="top-options">
        <button class="btn" id="new-rp" @click="createRp">
          Create New RP
        </button>
        <router-link class="btn" id="import-rp" to="/import">
          Import RP
        </router-link>
      </div>

      <!-- If no RPs, show "No recent RPs." -->
      <p v-if="!user.anon && rooms.length === 0">No recent RPs.</p>

      <!-- If no RPs and cannot create one, inform about how to get into an RP -->

      <!-- If no RPs, not logged in, but can login (SSL), suggest logging in -->
      <p v-if="user.anon && canLogin" id="login_register_button_div">
        <router-link to="/register" class="btn">Create an account</router-link> or <router-link to="/login" class="btn">log in</router-link> to create and keep track of all your RPs.
      </p>

      <!-- If there are RPs, list them -->
      <template v-for="room of rooms">
        <router-link :key="'room'+room.rpCode" class="pane recent-rp" :to="'/rp/'+room.rpCode">
          <div class="rp-color" :style="blockStyles(room)">
          </div>
          <div class="rp-title">
            <h4>{{ room.title }}</h4>
          </div>
          <span class="rp-date">{{ timeAgoText(room.updated) }}</span>
        </router-link>
      </template>
    </template>

  </div>
</template>

<style scoped>
  #homepage {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 5%;
    box-sizing: border-box;
  }
  header {
    padding: 10vh 0 1.7vh;
  }
  aside {
    transform: translate(calc(-20vmin - 32px)) rotate(-10deg);
    text-align: center;
    margin-bottom: 0;
    font-size: calc(16px + 7vmin);
    line-height: 1;
    font-family: "Alice";
  }

  header h1 {
      margin: -2vmin 0 0;
      text-align: center;
      font-size: calc(24px + 14vmin);
      line-height: 1;
      font-family: "Playfair Display";
      font-weight: normal;
  }

  h1 {
    margin: 30px auto 0;
    text-align: center;
    font-weight: normal;
    width: 100%;
  }
  h1::first-letter, .pane h4::first-letter{
    text-transform: capitalize;
  }
  #logout-button {
    margin: 7px auto 0;
    background: none;
    border: solid 1px rgba(0,0,0,0.5);
    padding: 5px 20px;
    border-radius: 5px;
    cursor: pointer;
  }
  #top-options {
    margin-top: 30px;
    display: flex;
    width: 300px;
    margin-bottom: 15px;
  }
  .btn {
    text-decoration: none;
  }
  .recent-rp {
    display: flex;
    flex-direction: column;
    width: 290px;
  }
  .rp-color {
    width: 5px;
    height: 100%;
    position: absolute;
  }
  .rp-date {
    width: 100%;
    text-align: right;
    display: block;
    position: absolute;
    font-size: 0.8em;
    top: 4px;
    right: 4px;
    opacity: 0.6;
  }
  .rp-title {
    padding: 10px;
  }
  #new-rp {
    height: 60px;
    font-size: inherit;
    flex-grow: 3;
  }
  #import-rp {
    height: 60px;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
  #login_register_button_div {
    margin-top: 30px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    width: 500px;
    align-items: center;
  }
  .pane {
    border: solid 1px rgba(0,0,0,0.5);
    background: none;
    font-family: inherit;
    cursor: pointer;
    margin: 0 5px 10px;
    text-decoration: none;
    color: inherit;
    box-sizing: border-box;
    border-radius: .25rem;
    overflow: hidden;
    position: relative;
    min-height: 40px;
  }
  .pane h4 {
    margin: 0.25rem 0;
  }
</style>

<script>
  import axios from 'axios';
  import coolstory from 'coolstory.js';

  export default {
    name: 'Dashboard',
    props: {
      user: Object,
      myUsername: String,
    },
    data() {
      return {
        loading: true,
        canLogin: (location.protocol === 'https:' || location.hostname === 'localhost'),
        canCreate: false,
        rooms: []
      };
    },
    beforeMount() {
      document.title = 'My RPs on ' + location.hostname;
      axios.post('/api/dashboard')
        .then(res => {
          this.canCreate = res.data.canCreate;
          this.rooms = res.data.rooms;
          this.loading = false;
        });
    },
    methods: {
      blockStyles(room) {
        const id = room.rpCode;
        // hash id into 32-bit integer
        var hash = 0, i, chr;
        for (i = 0; i < id.length; i++) {
          chr   = id.charCodeAt(i);
          hash  = ((hash << 5) - hash) + chr;
          hash |= 0; // Convert to 32bit integer
        }

        const [r, g, b] = [0,8,16].map(sh => hash >> sh & 255)
        const light = (r*299)+(g*587)+(b*114) > 128000;

        return {
          backgroundColor: `rgb(${r}, ${g}, ${b})`,
          color: light? 'black':'white'
        }
      },
      timeAgoText(timestamp) {
        const ago = Date.now() - new Date(timestamp).getTime();
        if (ago < 1000 * 60){
          return "less than a minute ago";
        }
        if (ago < 1000 * 60 * 60){
          var t = Math.trunc(ago / 60000);
          if (t == 1) {
            return "1 minute ago";
          } 
          return t + " minutes ago";
        }
        if (ago < 1000 * 60 * 60 * 24) {
          var t = Math.trunc(ago / 3600000);
          if (t == 1) {
            return "1 hour ago";
          } 
          return t + " hours ago";
        }
        if (ago < 1000 * 60 * 60 * 24 * 7) {
          var t = Math.trunc(ago / 86400000);
          if (t == 1) {
            return "1 day ago";
          } 
          return t + " days ago";
        } else {
          return new Date(timestamp).toLocaleDateString();
        }
      },
      createRp() {
        this.title = prompt("What is this RP called?");

        if (this.title == null) return;

        this.submitRp();
      },
      spinTitle() {
        let millis = 10.0;

        const changeTitle = () => this.title = coolstory.title(20);

        while ((millis *= 1.15) < 200.0 / .15) {
          setTimeout(changeTitle, millis);
        }
      },
      submitRp() {
        this.loading = true;

        axios.post('/api/rp', { title: this.title })
          .then(res => this.$router.push('/rp/' + res.data.rpCode))
          .catch((err) => {
            this.loading = false;
            alert('Failed to create RP: (' + err + ')');
          });
      },
      confirmLogout() {
        if (confirm('Log out?')) {
          this.$emit('logout')
        }
      }
    }
  };
</script>

